import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CTOKEN = "customer_token";

// Dedicated axios instance for customer endpoints (separate from the admin
// global axios defaults). Sends the customer JWT and cookies (Google session).
export const capi = axios.create({ baseURL: API, withCredentials: true });
capi.interceptors.request.use((config) => {
  const t = localStorage.getItem(CTOKEN);
  if (t) config.headers["Authorization"] = `Bearer ${t}`;
  return config;
});

const CustomerContext = createContext(null);

export function CustomerProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [ready, setReady] = useState(false);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await capi.get("/customer/me");
      setCustomer(data);
      return data;
    } catch {
      setCustomer(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const run = async () => {
      const hash = window.location.hash || "";
      if (hash.includes("session_id=")) {
        const sid = new URLSearchParams(hash.slice(1)).get("session_id");
        if (sid) {
          try {
            const { data } = await capi.post("/customer/google-session", { session_id: sid });
            setCustomer(data.user);
            window.dispatchEvent(new Event("gmjp_customer_login"));
          } catch { /* ignore invalid session */ }
        }
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        setReady(true);
        return;
      }
      await refreshMe();
      setReady(true);
    };
    run();
  }, [refreshMe]);

  const register = useCallback(async (payload) => {
    const { data } = await capi.post("/customer/register", payload);
    localStorage.setItem(CTOKEN, data.access_token);
    setCustomer(data.user);
    window.dispatchEvent(new Event("gmjp_customer_login"));
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await capi.post("/customer/login", { email, password });
    localStorage.setItem(CTOKEN, data.access_token);
    setCustomer(data.user);
    window.dispatchEvent(new Event("gmjp_customer_login"));
    return data.user;
  }, []);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try { await capi.post("/customer/logout"); } catch { /* ignore */ }
    localStorage.removeItem(CTOKEN);
    setCustomer(null);
    window.dispatchEvent(new Event("gmjp_customer_logout"));
  }, []);

  return (
    <CustomerContext.Provider value={{ customer, ready, register, login, loginWithGoogle, logout, refreshMe }}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error("useCustomer must be used within CustomerProvider");
  return ctx;
}
